import * as React from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./card"
import { Button } from "./button"
import { Input } from "./input"
import { Label } from "./label"
import { Textarea } from "./textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"
import { Badge } from "./badge"
import { Checkbox } from "./checkbox"
import { Loader2 } from "lucide-react"

// Import existing MedicationOption to avoid duplicates
interface MedicationOption {
  id: string
  name: string
  brand_name?: string
  category: string
  available_dosages?: string[]
}

export interface MedicationAdjustmentData {
  id: string
  medication_id: string
  medication_name: string
  preferred_dosage: string
  frequency?: string
  status: 'pending' | 'approved' | 'denied' | 'discontinued'
  requested_date?: string
  providerNotes?: string
  faxed?: string | null
  supply_days?: number
  other_dosage?: string
}

export interface MedicationAdjustmentFormProps {
  medication: MedicationAdjustmentData
  availableMedications?: MedicationOption[]
  onChange: (medication: MedicationAdjustmentData) => void
  onSave?: () => void
  onFax?: (shouldFax: boolean) => Promise<void>
  isSaving?: boolean
  showSaveButton?: boolean
  showRequestedDate?: boolean
  showFaxCheckbox?: boolean
  showMedicationSelector?: boolean
  className?: string
}

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
  approved: 'bg-green-100 text-green-800 hover:bg-green-200',
  denied: 'bg-red-100 text-red-800 hover:bg-red-200',
  discontinued: 'bg-gray-100 text-gray-800 hover:bg-gray-200'
}

const statusLabels = {
  pending: 'Pending',
  approved: 'Approved',
  denied: 'Denied',
  discontinued: 'Discontinued'
}

export function MedicationAdjustmentForm({
  medication,
  availableMedications = [],
  onChange,
  onSave,
  onFax,
  isSaving = false,
  showSaveButton = true,
  showRequestedDate = true,
  showFaxCheckbox = false,
  showMedicationSelector = false,
  className
}: MedicationAdjustmentFormProps) {
  
  // Internal state for form fields to ensure immediate UI updates
  const [formData, setFormData] = React.useState<MedicationAdjustmentData>(medication)
  
  // Update internal state when medication prop changes (for external updates)
  React.useEffect(() => {
    setFormData(medication)
  }, [medication])
  
  // Debounced onChange to prevent excessive parent updates
  const debouncedOnChange = React.useMemo(() => {
    let timeoutId: NodeJS.Timeout
    return (updatedData: MedicationAdjustmentData) => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        onChange(updatedData)
      }, 100) // 100ms debounce
    }
  }, [onChange])
  
  const handleFieldChange = React.useCallback((field: keyof MedicationAdjustmentData, value: any) => {
    const updatedData = {
      ...formData,
      [field]: value
    }
    
    // Debug logging for other_dosage field changes
    if (field === 'other_dosage') {
      console.log('🔍 MEDICATION FORM DEBUG: other_dosage field changed')
      console.log('🔍 MEDICATION FORM DEBUG: New value:', value)
      console.log('🔍 MEDICATION FORM DEBUG: Value type:', typeof value)
      console.log('🔍 MEDICATION FORM DEBUG: Updated data other_dosage:', updatedData.other_dosage)
    }
    
    // Update internal state immediately for responsive UI
    setFormData(updatedData)
    
    // Notify parent with debounce
    debouncedOnChange(updatedData)
  }, [formData, debouncedOnChange])

  const handleMedicationChange = React.useCallback((medicationId: string) => {
    const selectedMed = availableMedications.find(med => med.id === medicationId)
    if (selectedMed) {
      const updatedData = {
        ...formData,
        medication_id: selectedMed.id,
        medication_name: selectedMed.name,
        // Reset dosage when medication changes since different medications have different dosages
        preferred_dosage: selectedMed.available_dosages?.[0] || ''
      }
      
      // Update internal state immediately for responsive UI
      setFormData(updatedData)
      
      // Notify parent with debounce
      debouncedOnChange(updatedData)
    }
  }, [availableMedications, formData, debouncedOnChange])

  const handleFaxChange = React.useCallback(async (checked: boolean) => {
    if (onFax) {
      try {
        await onFax(checked)
      } catch (error) {
        console.error('Error handling fax change:', error)
      }
    }
  }, [onFax])

  // Get available dosages for the currently selected medication
  const selectedMedication = availableMedications.find(med => med.id === formData.medication_id)
  const availableDosages = selectedMedication?.available_dosages || []

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{formData.medication_name}</CardTitle>
          <Badge 
            variant="secondary" 
            className={statusColors[formData.status]}
          >
            {statusLabels[formData.status]}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Medication Selector - Only show when enabled */}
        {showMedicationSelector && availableMedications.length > 0 && (
          <div>
            <Label className="text-sm font-medium">
              Medication 
              {formData.medication_name === 'Doctor Recommendation' && (
                <span className="text-xs text-amber-600 ml-2">(Select actual medication)</span>
              )}
            </Label>
            <Select 
              value={formData.medication_id} 
              onValueChange={handleMedicationChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select medication">
                  {selectedMedication && (
                    <span className="truncate">{selectedMedication.name}</span>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-w-[400px]">
                {availableMedications.map((medication) => (
                  <SelectItem key={medication.id} value={medication.id}>
                    <div className="flex flex-col">
                      <span className="font-medium">{medication.name}</span>
                      {medication.brand_name && medication.brand_name !== medication.name && (
                        <span className="text-xs text-muted-foreground">{medication.brand_name}</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className="text-sm font-medium">Status</Label>
            <Select 
              value={formData.status} 
              onValueChange={(value) => handleFieldChange('status', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="denied">Denied</SelectItem>
                <SelectItem value="discontinued">Discontinued</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium">Dosage</Label>
            {availableDosages.length > 0 ? (
              <Select
                value={formData.preferred_dosage}
                onValueChange={(value) => handleFieldChange('preferred_dosage', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select dosage" />
                </SelectTrigger>
                <SelectContent>
                  {availableDosages.map((dosage) => (
                    <SelectItem key={dosage} value={dosage}>
                      {dosage}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={formData.preferred_dosage}
                onChange={(e) => handleFieldChange('preferred_dosage', e.target.value)}
                placeholder="Enter dosage"
              />
            )}
          </div>
          <div>
            <Label className="text-sm font-medium">Other Dosage</Label>
            <Input
              value={formData.other_dosage || ''}
              onChange={(e) => handleFieldChange('other_dosage', e.target.value)}
              placeholder="Alternative dosage"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Frequency</Label>
            <Input
              value={formData.frequency || ''}
              onChange={(e) => handleFieldChange('frequency', e.target.value)}
              placeholder="Enter frequency"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium">Supply (Days)</Label>
            <Input
              type="number"
              value={formData.supply_days || ''}
              onChange={(e) => handleFieldChange('supply_days', e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder="Enter supply duration (e.g., 30, 60, 90)"
              min="1"
              max="365"
            />
          </div>
          {showRequestedDate && formData.requested_date && (
            <div>
              <Label className="text-sm font-medium">Requested Date</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {new Date(formData.requested_date).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
        
        <div>
          <Label className="text-sm font-medium">Provider Notes</Label>
          <Textarea
            value={formData.providerNotes || ''}
            onChange={(e) => handleFieldChange('providerNotes', e.target.value)}
            placeholder="Add provider notes..."
            rows={3}
          />
        </div>

        {/* Faxed checkbox */}
        {showFaxCheckbox && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`faxed-clinical-${formData.id}`}
              checked={!!formData.faxed}
              onCheckedChange={handleFaxChange}
              disabled={isSaving}
            />
            <Label htmlFor={`faxed-clinical-${formData.id}`} className="text-sm font-medium">
              Faxed to Pharmacy
              {formData.faxed && formData.faxed !== 'pending' && (
                <span className="text-xs text-muted-foreground ml-2">
                  (Faxed on {(() => {
                    const date = new Date(formData.faxed);
                    return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleDateString();
                  })()})
                </span>
              )}
            </Label>
          </div>
        )}

        {showSaveButton && onSave && (
          <div className="flex justify-end pt-2">
            <Button 
              onClick={onSave}
              disabled={isSaving}
              className="w-32"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}